// What the selected club has, beyond the pages every club gets.
//
// Two different things decide what a club sees:
//
//   Its ORGANISATION TYPE (athletics club or school) decides whether it has
//   visitors, multi-club meets, teams and adult athletes. The API works
//   these out (OrganizationFeatures) and sends them as flags on the club:
//   hasVisitors, hasMultiClubMeets, hasTeams, hasAdultAthletes. Nothing in
//   the website tests organizationType itself; it reads the flags through
//   this file.
//
//   Its GROUPING (age or year level) decides the words and labels a user
//   reads. That is utils/groupLabel.js.
//
// Guests are not here because no club has them: the Guest pages, and the
// choice of Guest when adding a member or an athlete, are hidden for every
// club (Doug, 2026-10-11). A member or athlete who is already a Guest still
// shows as one and can be changed.
//
// A club with no flag (no club selected yet, or an API older than the flag)
// is treated as an athletics club is today, so nothing is hidden by mistake.
export const clubFeaturesOf = (club) => ({
  visitors: club?.hasVisitors !== false,
  multiClubMeets: club?.hasMultiClubMeets !== false,
  teams: club?.hasTeams === true,
  // Adults are placed in their own race, not within their age. A school has
  // none: every student is placed by age (utils/groupLabel.js,
  // oldestGroupPlacedByAge).
  adultAthletes: club?.hasAdultAthletes !== false,
  // Its invitations offer the Club Connect app as well as the website. A
  // school's do not (Doug, 2026-10-21: "For schools we should not include the
  // continue on the app as part of the invitation message. This is only for
  // clubs."). The API sends it as clubConnectInInvitations.
  clubConnectInInvitations: club?.clubConnectInInvitations !== false,
  // Is offered season-best points in its Points settings. A school is not
  // (the points design, 3.7). The API sends it as hasSeasonBestPoints.
  seasonBestPoints: club?.hasSeasonBestPoints !== false,
});

// The line under Send Invitation and Resend Invitation (Club Members, a
// member's page): what the email lets the person reach. It names the Club
// Connect app only for a club whose invitations offer it, as the email does
// (Doug, 2026-10-22).
export const invitationAccessText = (club) =>
  clubFeaturesOf(club).clubConnectInInvitations
    ? 'An email will be sent asking them to confirm their email address and set a password to access the Results Portal Website and Club Connect App.'
    : 'An email will be sent asking them to confirm their email address and set a password to access the Results Portal Website.';

// The organisation in a sentence: "club" or "school" ("Use the school's points"). The API sends it
// as organisationWord, from the same words a school's invitation email uses (InvitationWords).
export const organisationWordOf = (club) => club?.organisationWord || 'club';

// The same word in a title: "Club Records" or "School Records", "Club Settings" or "School
// Settings" (Doug, 2026-10-26, school words pass 1).
export const organisationTitleOf = (club) => {
  const word = organisationWordOf(club);
  return word.charAt(0).toUpperCase() + word.slice(1);
};

// The club a screen is working for, when the screen takes its club from the
// web address and not from the club switcher (the meet screens do). It is
// looked up in the club list already in memory, so no request is made; the
// selected club is the fallback while that list is still loading.
export const clubForId = (userState, clubId) =>
  userState?.clubs?.find((club) => club.clubID === clubId) ||
  userState?.selectedClub ||
  null;

// ---- Meet types ----
//
// A meet is a single-club competition, a multi-club competition or a training
// session. The numbers are the API's MeetTypes.
export const MEET_TYPE = { SINGLE_CLUB: 1, MULTI_CLUB: 2, TRAINING: 3 };

// What each kind of place calls the three types. The words differ from place
// to place and always have; this table is where they are kept.
//
// A club that cannot hold a multi-club meet (a school) has only one kind of
// competition, so it reads plain "Competition" where a club reads "Club
// Competition" (Doug, 2026-10-11: "Competition for schools is the same as
// single club competition for clubs"). That is the second word of each pair
// below.
const MEET_TYPE_WORDS = {
  // The Meet Type list on New Meet and Edit Meet, and Default Meet Type in
  // Club Settings. A club reads Club Competition (Doug, 2026-10-26).
  form: {
    [MEET_TYPE.SINGLE_CLUB]: ['Club Competition', 'Competition'],
    [MEET_TYPE.MULTI_CLUB]: 'Multi Club Competition',
    [MEET_TYPE.TRAINING]: 'Training Session',
  },
  // The Type row and the tag on Meet Details, and the tag on a dashboard
  // meet card. A club reads Club Competition, as in the list (Doug,
  // 2026-10-26).
  detail: {
    [MEET_TYPE.SINGLE_CLUB]: ['Club Competition', 'Competition'],
    [MEET_TYPE.MULTI_CLUB]: 'Multi club meet',
    [MEET_TYPE.TRAINING]: 'training',
  },
  // The Meet Type filter on the Meets page and in Copy From Meet. A club
  // reads Club Competition, as in the list (Doug, 2026-10-26).
  filter: {
    [MEET_TYPE.SINGLE_CLUB]: ['Club Competition', 'Competition'],
    [MEET_TYPE.MULTI_CLUB]: 'Multi Club',
    [MEET_TYPE.TRAINING]: 'Training',
  },
  // The section headings on the Meets page.
  heading: {
    [MEET_TYPE.SINGLE_CLUB]: ['Club Competitions', 'Competitions'],
    [MEET_TYPE.MULTI_CLUB]: 'Multi-club Competitions',
    [MEET_TYPE.TRAINING]: 'Training Sessions',
  },
};

// What a club calls a meet type in one kind of place (a key of
// MEET_TYPE_WORDS). A type that is not one of the three is named as a
// multi-club meet, which is what every screen did before this table existed.
export const meetTypeName = (club, meetType, place) => {
  const words = MEET_TYPE_WORDS[place];
  const name = words[Number(meetType)] ?? words[MEET_TYPE.MULTI_CLUB];
  if (!Array.isArray(name)) return name;
  return clubFeaturesOf(club).multiClubMeets ? name[0] : name[1];
};

// The meet types a club may choose: all three, or a competition and a
// training session for a club that cannot hold a multi-club meet.
export const meetTypesFor = (club) =>
  clubFeaturesOf(club).multiClubMeets
    ? [MEET_TYPE.SINGLE_CLUB, MEET_TYPE.MULTI_CLUB, MEET_TYPE.TRAINING]
    : [MEET_TYPE.SINGLE_CLUB, MEET_TYPE.TRAINING];

// The same as options for a dropdown, named for one kind of place.
// keepType: a type the record on screen already has. It is offered even when
// the club may not choose it, so an existing value is shown and is not
// changed just by saving the form.
export const meetTypeOptionsFor = (club, place, keepType) => {
  const types = meetTypesFor(club);
  const kept = Number(keepType);
  const offered =
    Object.values(MEET_TYPE).includes(kept) && !types.includes(kept)
      ? [...types, kept].sort()
      : types;
  return offered.map((value) => ({
    value,
    label: meetTypeName(club, value, place),
  }));
};
